import type { Keyword } from '@/types/Keyword';
import type { IProject } from '@/interfaces/project.interface';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

export async function getUsersProjects(options: {
  userId?: string;
  organizationId?: string;
  accessToken?: string;
}) {
  const searchParams = new URLSearchParams();
  if (options.userId) {
    searchParams.append('userId', options.userId);
  }
  if (options.organizationId) {
    searchParams.append('organizationId', options.organizationId);
  }
  const res = await fetch(
    `${process.env.NEXT_PUBLIC_API_DEVELOPMENT_ENDPOINT}/projects?${searchParams.toString()}`,
    {
      cache: 'no-store',
      headers: {
        Authorization: `Bearer ${options?.accessToken}`,
      },
      next: {
        tags: ['PROFILE_PAGE', searchParams.toString()],
      },
    }
  );
  const data: IProject[] = (await res.json())[0];

  return data;
}

export async function getUsersProtectedProjects(
  userId: string,
  options: {
    accessToken: string;
  }
) {
  const res = await fetch(
    `${process.env.NEXT_PUBLIC_API_DEVELOPMENT_ENDPOINT}/projects/protected?userId=${userId}`,
    {
      cache: 'no-store',
      headers: {
        Authorization: `Bearer ${options.accessToken}`,
      },
      next: {
        tags: ['PROFILE_PAGE', userId],
      },
    }
  );
  const data: IProject[] = (await res.json())[0];

  return data;
}

export type ProjectSearchResultType = {
  results: IProject[];
  total: number;
  selectedSkillsNames: Keyword[];
  skillsCount: number;
};

export async function searchProjects(args: {
  term: string;
  pageSize: number;
  page: number;
  sdgs?: string[];
  lat?: number;
  lon?: number;
  searchRadius?: number;
  skills?: Keyword[];
}) {
  const searchParams = new URLSearchParams();
  searchParams.append('term', args.term);
  searchParams.append('pageSize', args.pageSize.toString());
  searchParams.append('page', args.page.toString());

  if (args.sdgs) {
    searchParams.append('sdgs', args.sdgs ? args.sdgs.join(',') : '');
  }
  if (args.skills) {
    searchParams.append(
      'skills',
      args.skills ? args.skills.map((skill) => skill.id).join(',') : ''
    );
  }
  if (args.lat && args.lon) {
    searchParams.append('lat', args.lat.toString());
    searchParams.append('lon', args.lon.toString());
    searchParams.append('searchRadius', args.searchRadius?.toString() || '20');
  }

  const res = await fetch(
    `${
      process.env.NEXT_PUBLIC_API_DEVELOPMENT_ENDPOINT
    }/projects/search?${searchParams.toString()}`,
    {
      cache: 'no-store',
      next: {
        tags: ['SEARCH', searchParams.toString()],
      },
    }
  );
  const data: ProjectSearchResultType = await res.json();
  return data;
}

export function useSearch(props: {
  term: string;
  sdgs?: string[];
  skills?: Keyword[];
  lat?: number;
  lon?: number;
  searchRadius?: number;
  initialData?: ProjectSearchResultType;
}) {
  return useInfiniteQuery({
    queryKey: [
      'SEARCH',
      {
        term: props.term,
        sdgs: props.sdgs,
        skills: props.skills,
        lat: props.lat,
        lon: props.lon,
        searchRadius: props.searchRadius,
      },
    ],
    queryFn: ({ pageParam }) =>
      searchProjects({
        term: props.term,
        pageSize: 24,
        page: pageParam,
        sdgs: props.sdgs,
        skills: props.skills,
        lat: props.lat,
        lon: props.lon,
        searchRadius: props.searchRadius,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.results?.length < 12) {
        return null;
      }

      return pages.length + 1;
    },
    refetchOnMount: false,
    refetchOnReconnect: false,
    ...(props.initialData
      ? {
          initialData: {
            pages: [props.initialData],
            pageParams: [1],
          },
        }
      : {}),
  });
}

export function useUsersProjects(userId: string, enabled = true) {
  return useQuery({
    queryKey: ['USERS_PROJECTS', userId],
    queryFn: async () =>
      getUsersProjects({
        userId,
      }),
    enabled,
  });
}
